// Updated DataStores listing page with real API integration
import React, { useCallback, useState, useEffect } from 'react';
import { Pencil, Trash2, PlusCircle, DatabaseZap, UserCog } from 'lucide-react';
import AddVectorStoreModal from './VectorStore/AddVectorStoreModal';
import { ingestionService } from '../../../components/api/ingestionService';
import PermissionsAssignmentModal from '../common/Permissions/PermissionsAssignmentModal';

const DataStores = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any | null>(null);
  const [datastoresList, setDatastoresList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ingestionService.getVectorStores();
      setDatastoresList(Array.isArray(res) ? res : []);
    } catch {
      setError('Failed to load vector datastores.');
      setDatastoresList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);
  const [showAssignmentModal, setAssignmentModal] = useState(false);

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white">Vector Datastores</h2>
          <p className="text-sm text-gray-400">
            Manage vector databases and embedding repositories across tenants and use cases.
          </p>
        </div>
        <button
          onClick={() => { setEditingItem(null); setShowAddModal(true); }}
          className="flex items-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm shadow"
        >
          <PlusCircle className="w-4 h-4 mr-2" /> Add New Datastore
        </button>
      </div>

      {error && <p className="mb-3 text-sm text-red-400">{error}</p>}

      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {loading ? (
          <div className="col-span-full text-center text-gray-500 py-10">Loading…</div>
        ) : (
        datastoresList.map((ds) => (
          <div
            key={ds.id}
            className="bg-neutral-900 border border-neutral-800 rounded-lg p-5 shadow hover:border-indigo-500 transition-all"
          >
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-2">
                <DatabaseZap className="w-5 h-5 text-indigo-400" />
                <h3 className="text-white font-medium text-base">{ds.description || ds.collectionName}</h3>
              </div>
              <span
                className={`text-xs px-2 py-1 rounded-full font-semibold ${ds.isActive ? 'bg-green-700 text-green-100' : 'bg-red-700 text-red-100'}`}
              >
                {ds.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>

            <div className="text-sm text-gray-300 space-y-1">
              <p>
                <span className="text-gray-400">Provider:</span> {ds.provider ?? '—'}
              </p>
              <p>
                <span className="text-gray-400">Collection:</span> {ds.collectionName ?? '—'}
              </p>
              <p>
                <span className="text-gray-400">Dimensions:</span> {ds.vectorDimension ?? '—'}
              </p>
              <p>
                <span className="text-gray-400">Endpoint:</span>{' '}
                <span className="font-mono text-xs truncate block max-w-[200px]" title={ds.endpointUrl}>
                  {ds.endpointUrl ? `${ds.endpointUrl.slice(0, 40)}${ds.endpointUrl.length > 40 ? '…' : ''}` : '—'}
                </span>
              </p>
              <p>
                <span className="text-gray-400">Created By:</span> {ds.createdBy ?? '—'}
              </p>
              <p>
                <span className="text-gray-400">Created On:</span>{' '}
                <span className="font-mono text-xs">{ds.createdAt ? new Date(ds.createdAt).toLocaleDateString() : '—'}</span>
              </p>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button
                className="text-indigo-400 hover:text-indigo-300 transition-colors"
                title="Manage Permissions & Access"
                onClick={() => setAssignmentModal(true)}
              >
                <UserCog className="w-4 h-4" />
              </button>
              <button
                className="text-yellow-400 hover:text-yellow-300 transition-colors"
                title="Edit"
                onClick={() => { setEditingItem(ds); setShowAddModal(true); }}
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                className="text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                title="Delete"
                disabled={deletingId === ds.id}
                onClick={async () => {
                  if (!window.confirm(`Delete "${ds.description || ds.collectionName || 'this datastore'}"? This cannot be undone.`)) return;
                  setDeletingId(ds.id);
                  setError(null);
                  try {
                    await ingestionService.deleteVectorStore(ds.id);
                    await fetchList();
                  } catch (e: any) {
                    setError(e?.response?.data?.message || e?.message || 'Failed to delete.');
                  } finally {
                    setDeletingId(null);
                  }
                }}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))
        )}
      </div>

      {!loading && datastoresList.length === 0 && (
        <div className="text-center text-gray-500 py-10">No datastores found.</div>
      )}

      {showAddModal && (
        <AddVectorStoreModal
          initialData={editingItem}
          onClose={() => { setShowAddModal(false); setEditingItem(null); }}
          onSubmit={async (data) => {
            setError(null);
            const providerMap: Record<string, string> = {
              Pinecone: 'Pinecone',
              Qdrant: 'Qdrant',
              ChromaDB: 'Chroma',
              Weaviate: 'Custom',
              Milvus: 'Milvus',
              FAISS: 'Custom',
            };
            const dto = {
              provider: providerMap[data.type] || data.type || 'Custom',
              apiKey: data.apiKey || undefined,
              endpointUrl: data.endpoint || undefined,
              collectionName: data.name?.trim() || 'Unnamed',
              isDefault: false,
              isActive: data.status === 'Active',
              description: data.description || undefined,
              region: data.region || undefined,
              intendedUsage: data.intendedUsage || undefined,
              tenantOrClient: data.tenantClient || undefined,
            };
            if (editingItem?.id) {
              await ingestionService.updateVectorStore(editingItem.id, dto);
            } else {
              await ingestionService.createVectorStore(dto);
            }
            setShowAddModal(false);
            setEditingItem(null);
            await fetchList();
          }}
        />
      )}

      {showAssignmentModal && (
        <PermissionsAssignmentModal
          onClose={() => setAssignmentModal(false)}
          onSave={() => { }}
          entityType="vector-store"
          entityName="Vector Store"
        // Optional: Pass initial permissions
        // initialPermissions={{
        //     departments: [],
        //     users: [],
        //     roles: [],
        //     exclusions: []
        // }}
        />
      )}
    </div>
  );
};

export default DataStores;
